/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import Peer, { DataConnection } from 'peerjs';

interface PeerContextValue {
  connected: boolean;
  connect: (peerServerUrl: string, rosPeerId: string) => void;
  send: (tabIndex: number, message: any) => void;
  receive: (callback: (data: any) => void) => void;
}

const PeerContext = createContext<PeerContextValue | undefined>(undefined);

export const PeerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [peer, setPeer] = useState<Peer | null>(null);
  const [connected, setConnected] = useState(false);
  const [connections, setConnections] = useState<
    Record<string, { sender: DataConnection; receiver: DataConnection }>
  >({});
  const [connectionIndex, setConnectionIndex] = useState<string[]>([]);

  // Function to establish a connection to the PeerJS server
  const connect = (peerServerUrl: string, rosPeerId: string) => {
    try {
      console.log('Connecting...');
      const newPeer = new Peer('ros-receiver', { host: 'localhost', port: 9000, path: '/peerjs' });
      newPeer.on('open', (id) => {
        console.log('Connected');
        console.log(`App connected as receiver with ID: ${id}`);
      });

      newPeer.on('open', (id) => {
        console.log(`Connected to PeerJS server with ID: ${id}`);
        setPeer(newPeer);
        setConnected(true);
      });

      newPeer.on('connection', (conn) => {
        console.log(`Received connection from peer: ${conn.peer}`);

        conn.on('data', (data) => {
          const connectionToTab = newPeer.connect(conn.peer);
          connectionToTab.on('open', () => {
            console.log('App connected to tab-receiver as sender');
          });

          setConnections((prev) => ({
            ...prev,
            [conn.peer]: { receiver: conn, sender: connectionToTab },
          }));
          setConnectionIndex((prev) => [...prev, conn.peer]);
        });

        conn.on('close', () => {
          console.log(`Connection with ${conn.peer} closed`);
          setConnections((prev) => {
            const { [conn.peer]: _, ...remaining } = prev;
            return remaining;
          });
        });
      });

      newPeer.on('error', (err) => {
        console.error('PeerJS error:', err);
      });
    } catch (error) {
      console.log(JSON.stringify(error));
    }
  };

  // Function to send a message to a specific peer
  const send = (tabIndex: number, message: any) => {
    if (connections[connectionIndex[tabIndex]]) {
      connections[connectionIndex[tabIndex]].sender.send(message);
    } else {
      console.warn(`No connection found for peer ID: ${connectionIndex[tabIndex]}`);
    }
  };

  // Function to register a callback for receiving data
  const receive = (callback: (data: any) => void) => {
    if (peer) {
      peer.on('connection', (conn) => {
        conn.on('data', callback);
      });
    } else {
      console.warn('Peer is not connected yet.');
    }
  };

  return (
    <PeerContext.Provider value={{ connected, connect, send, receive }}>
      {children}
    </PeerContext.Provider>
  );
};

// Custom hook to use the PeerJS context
export const usePeer = () => {
  const context = useContext(PeerContext);
  if (!context) {
    throw new Error('usePeer must be used within a PeerProvider');
  }
  return context;
};
